import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Paper, TextField, Button, Typography,
    Box, Grid, Alert, MenuItem, InputAdornment, Autocomplete
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, People as PeopleIcon } from '@mui/icons-material';
import api from '../services/api';

function ClienteForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [error, setError] = useState('');
    const [clientes, setClientes] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [formData, setFormData] = useState({
        nome: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        telefone: '',
        telefone2: '',
        email: '',
        endereco: '',
        bairro: '',
        cidade: '',
        profissao: '',
        estado_civil: '',
        nacionalidade: '',
        sexo: '',
        idade: '',
        lembrete: '',
        data_obito: '',
        senha: '',
        pis_pasep: '',
        token: ''
    });
    const [generatingToken, setGeneratingToken] = useState(false);

    useEffect(() => {
        if (id) {
            loadCliente();
        } else {
            loadClientes();
        }
    }, [id]);

    const loadClientes = async () => {
        try {
            const response = await api.get('/clientes');
            setClientes(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        }
    };

    const formatarDataParaInput = (data) => {
        if (!data) return '';
        
        // Se está no formato YYYY-MM-DD (do banco), converte para DD/MM/YYYY
        if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}/.test(data)) {
            const partes = data.split('T')[0].split('-'); // Remove hora se houver
            if (partes.length === 3) {
                return `${partes[2]}/${partes[1]}/${partes[0]}`; // DD/MM/YYYY
            }
        }
        
        // Se já está em formato DD/MM/YYYY, retorna como está
        if (typeof data === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
            return data;
        }
        
        // Se não conseguir identificar, retorna a data original
        return data;
    };

    const loadCliente = async () => {
        try {
            const response = await api.get(`/clientes/${id}`);
            const dados = response.data;
            
            // A lógica de escolher data_nascimento ou data_nascimento_old já vem do backend
            // Se data_nascimento está vazio mas data_nascimento_old tem valor, o backend retorna data_nascimento_old em data_nascimento
            
            // Formata datas para o input (DD/MM/YYYY)
            if (dados.data_nascimento) {
                dados.data_nascimento = formatarDataParaInput(dados.data_nascimento);
            }
            if (dados.data_obito) {
                dados.data_obito = formatarDataParaInput(dados.data_obito);
            }
            
            setFormData(dados);
            
            // Calcula idade se tiver data de nascimento válida
            if (dados.data_nascimento) {
                dados.idade = calcularIdade(dados.data_nascimento);
                setFormData({ ...dados });
            }
        } catch (error) {
            console.error('Erro ao carregar cliente:', error);
            setError('Erro ao carregar dados do cliente');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            // Prepara dados para envio (converte DD/MM/YYYY para YYYY-MM-DD para o banco)
            const dadosParaEnvio = { ...formData };
            
            // Converte data de nascimento de DD/MM/YYYY para YYYY-MM-DD
            if (dadosParaEnvio.data_nascimento) {
                if (dadosParaEnvio.data_nascimento.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    const partes = dadosParaEnvio.data_nascimento.split('/');
                    dadosParaEnvio.data_nascimento = `${partes[2]}-${partes[1]}-${partes[0]}`;
                }
                // Se já está em YYYY-MM-DD, mantém como está
                // Se está em formato inválido, mantém como está (será salvo como texto)
            }
            
            // Mesma lógica para data de óbito
            if (dadosParaEnvio.data_obito) {
                if (dadosParaEnvio.data_obito.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    const partes = dadosParaEnvio.data_obito.split('/');
                    dadosParaEnvio.data_obito = `${partes[2]}-${partes[1]}-${partes[0]}`;
                }
            }

            if (id) {
                await api.put(`/clientes/${id}`, dadosParaEnvio);
            } else {
                await api.post('/clientes', dadosParaEnvio);
            }
            navigate('/clientes');
        } catch (error) {
            console.error('Erro ao salvar cliente:', error);
            setError(error.response?.data?.error || 'Erro ao salvar cliente');
        }
    };

    const calcularIdade = (dataNascimento) => {
        if (!dataNascimento) return '';
        
        try {
            const hoje = new Date();
            let nascimento;
            
            // Tenta parsear a data (formato brasileiro DD/MM/YYYY)
            if (typeof dataNascimento === 'string') {
                if (dataNascimento.includes('/')) {
                    // Formato DD/MM/YYYY
                    const partes = dataNascimento.split('/');
                    if (partes.length === 3) {
                        const dia = parseInt(partes[0]);
                        const mes = parseInt(partes[1]) - 1;
                        const ano = parseInt(partes[2]);
                        if (ano >= 1900 && ano <= 2100 && mes >= 0 && mes <= 11 && dia >= 1 && dia <= 31) {
                            nascimento = new Date(ano, mes, dia);
                        }
                    }
                } else if (dataNascimento.includes('-')) {
                    // Formato YYYY-MM-DD (do banco)
                    nascimento = new Date(dataNascimento);
                }
            }
            
            if (!nascimento || isNaN(nascimento?.getTime())) {
                return ''; // Data inválida
            }
            
            // Verifica se não está no futuro
            if (nascimento > hoje) {
                return ''; // Data no futuro
            }
            
            let idade = hoje.getFullYear() - nascimento.getFullYear();
            const mes = hoje.getMonth() - nascimento.getMonth();
            
            if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
                idade--;
            }
            
            return idade >= 0 ? idade.toString() : '';
        } catch (error) {
            return ''; // Erro ao calcular, retorna vazio
        }
    };

    const handleChange = (e) => {
        let valor = e.target.value;
        
        // Formata data de nascimento enquanto digita (DD/MM/AAAA)
        if (e.target.name === 'data_nascimento') {
            // Remove tudo que não é número
            valor = valor.replace(/\D/g, '');
            // Adiciona barras automaticamente
            if (valor.length > 2) {
                valor = valor.substring(0, 2) + '/' + valor.substring(2);
            }
            if (valor.length > 5) {
                valor = valor.substring(0, 5) + '/' + valor.substring(5, 9);
            }
        }
        
        const newFormData = {
            ...formData,
            [e.target.name]: valor
        };
        
        // Calcula idade automaticamente quando data de nascimento muda (apenas se estiver completa)
        if (e.target.name === 'data_nascimento' && valor.length === 10) {
            newFormData.idade = calcularIdade(valor);
        }
        
        setFormData(newFormData);
    };

    const handleSearchSelect = (cliente) => {
        if (cliente) {
            // Se não estiver editando, redireciona para edição do cliente encontrado
            if (!id) {
                navigate(`/clientes/editar/${cliente.id}`);
            } else {
                // Se estiver editando, apenas atualiza o valor de busca
                setSearchValue(`${cliente.nome} - ${cliente.cpf || ''}`);
            }
        }
    };

    const handleGenerateToken = async () => {
        if (!id) {
            alert('Salve o cliente primeiro para gerar um token');
            return;
        }
        setGeneratingToken(true);
        try {
            const response = await api.post(`/clientes/${id}/gerar-token`);
            setFormData({ ...formData, token: response.data.token });
            alert(`Token gerado: ${response.data.token}`);
        } catch (error) {
            console.error('Erro ao gerar token:', error);
            setError(error.response?.data?.error || 'Erro ao gerar token');
        } finally {
            setGeneratingToken(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2,
                    mt: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <PeopleIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    {id ? 'Editar Cliente' : 'Novo Cliente'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    {id ? 'Atualize as informações do cliente' : 'Cadastre um novo cliente'}
                </Typography>
            </Box>

            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: 2 }}>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!id && (
                    <Box sx={{ mb: 3 }}>
                        <Autocomplete
                            options={clientes}
                            getOptionLabel={(option) => `${option.nome} - ${option.cpf || 'Sem CPF'}`}
                            value={undefined}
                            onChange={(e, newValue) => {
                                if (newValue) {
                                    handleSearchSelect(newValue);
                                }
                            }}
                            onInputChange={(e, newInputValue) => {
                                setSearchValue(newInputValue);
                            }}
                            inputValue={searchValue}
                            filterOptions={(options, { inputValue }) => {
                                if (!inputValue) return [];
                                const searchValue = inputValue.toLowerCase();
                                return options.filter(option => {
                                    return (
                                        option.nome?.toLowerCase().includes(searchValue) ||
                                        option.cpf?.toLowerCase().includes(searchValue) ||
                                        option.email?.toLowerCase().includes(searchValue)
                                    );
                                }).slice(0, 10); // Limita a 10 resultados
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Buscar Cliente Existente"
                                    placeholder="Digite nome, CPF ou email para buscar..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText="Nenhum cliente encontrado"
                            clearOnEscape
                        />
                    </Box>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Nome"
                                name="nome"
                                value={formData.nome}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="CPF"
                                name="cpf"
                                value={formData.cpf}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="RG"
                                name="rg"
                                value={formData.rg}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Data de Nascimento"
                                name="data_nascimento"
                                type="text"
                                value={formData.data_nascimento || ''}
                                onChange={handleChange}
                                placeholder="dd/mm/aaaa"
                                helperText="Formato: DD/MM/AAAA"
                                inputProps={{
                                    maxLength: 10,
                                    pattern: "\\d{2}/\\d{2}/\\d{4}"
                                }}
                                onBlur={(e) => {
                                    // Valida e formata ao sair do campo
                                    const valor = e.target.value.replace(/\D/g, '');
                                    if (valor.length === 8) {
                                        const formatado = `${valor.substring(0, 2)}/${valor.substring(2, 4)}/${valor.substring(4, 8)}`;
                                        setFormData({ ...formData, data_nascimento: formatado });
                                        // Recalcula idade
                                        setFormData(prev => ({ ...prev, idade: calcularIdade(formatado) }));
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telefone"
                                name="telefone"
                                value={formData.telefone}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telefone 2"
                                name="telefone2"
                                value={formData.telefone2}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                helperText="Necessário para consulta de processos pelo cliente"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Senha INSS"
                                name="senha"
                                type="text"
                                value={formData.senha || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        {id && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Token de Consulta"
                                    name="token"
                                    value={formData.token || ''}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    size="small"
                                                    onClick={handleGenerateToken}
                                                    disabled={generatingToken}
                                                    startIcon={<RefreshIcon />}
                                                >
                                                    {formData.token ? 'Regenerar' : 'Gerar'}
                                                </Button>
                                            </InputAdornment>
                                        )
                                    }}
                                    helperText="Token de 6 dígitos para o cliente consultar seus processos"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sexo"
                                name="sexo"
                                select
                                value={formData.sexo || ''}
                                onChange={handleChange}
                            >
                                <MenuItem value="">Selecione</MenuItem>
                                <MenuItem value="M">Masculino</MenuItem>
                                <MenuItem value="F">Feminino</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Endereço"
                                name="endereco"
                                value={formData.endereco}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Bairro/Distrito"
                                name="bairro"
                                value={formData.bairro}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Cidade"
                                name="cidade"
                                value={formData.cidade}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Nº do PIS/PASEP"
                                name="pis_pasep"
                                value={formData.pis_pasep || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Idade"
                                name="idade"
                                type="number"
                                value={formData.idade}
                                onChange={handleChange}
                                InputProps={{
                                    readOnly: true
                                }}
                                helperText="Calculada automaticamente pela data de nascimento"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Profissão"
                                name="profissao"
                                value={formData.profissao}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Estado Civil"
                                name="estado_civil"
                                value={formData.estado_civil}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Nacionalidade"
                                name="nacionalidade"
                                value={formData.nacionalidade}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Lembrete"
                                name="lembrete"
                                value={formData.lembrete}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Data de Óbito"
                                name="data_obito"
                                type="date"
                                value={formData.data_obito}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth={false}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            Salvar
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/clientes')}
                            fullWidth={false}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

export default ClienteForm; 